import Papa, { ParseResult as PapaParseResult } from 'papaparse';
import { Candle, OptionRow } from '../../types';

export interface ParseResult<T> {
  data: T[];
  errors: string[];
  warnings: string[];
}

// ─── OHLCV Parser ─────────────────────────────────────────────────────────────
/**
 * Accepted column names (case-insensitive, underscore/space flexible):
 *   timestamp | datetime | time | date
 *   open
 *   high
 *   low
 *   close | last | price
 *   volume | vol (optional)
 */
export function parseOHLCVCsv(text: string): ParseResult<Candle> {
  const errors: string[] = [];
  const warnings: string[] = [];

  const parsed: PapaParseResult<Record<string, string>> = Papa.parse(text.trim(), {
    header: true, skipEmptyLines: true, dynamicTyping: false
  });

  if (parsed.errors.length) {
    warnings.push(...parsed.errors.slice(0, 3).map(e => `CSV parse warning row ${e.row}: ${e.message}`));
  }

  const headers = parsed.meta.fields?.map(h => h.toLowerCase().replace(/[\s_'"]+/g, '_')) || [];
  const findCol = (...names: string[]) => {
    for (const name of names) {
      const idx = headers.findIndex(h => h === name || h.includes(name));
      if (idx >= 0) return parsed.meta.fields![idx];
    }
    return null;
  };

  const tCol = findCol('timestamp', 'datetime', 'time', 'date', 'datetime64');
  const oCol = findCol('open');
  const hCol = findCol('high');
  const lCol = findCol('low');
  const cCol = findCol('close', 'last', 'price');
  const vCol = findCol('volume', 'vol');

  const missing: string[] = [];
  if (!tCol) missing.push('timestamp/date');
  if (!oCol) missing.push('open');
  if (!hCol) missing.push('high');
  if (!lCol) missing.push('low');
  if (!cCol) missing.push('close');
  if (missing.length) {
    return { data: [], errors: [`Missing required columns: ${missing.join(', ')}. Found: ${headers.join(', ')}`], warnings };
  }

  const candles: Candle[] = [];
  for (let i = 0; i < parsed.data.length; i++) {
    const row = parsed.data[i];
    const tsStr = row[tCol!];
    const ts = parseTimestamp(tsStr);
    if (!ts) { errors.push(`Row ${i + 2}: cannot parse timestamp "${tsStr}"`); continue; }

    const o = parseFloat(row[oCol!]);
    const h = parseFloat(row[hCol!]);
    const l = parseFloat(row[lCol!]);
    const c = parseFloat(row[cCol!]);
    const v = vCol ? parseFloat(row[vCol]) || 0 : 0;

    if ([o, h, l, c].some(isNaN)) { errors.push(`Row ${i + 2}: non-numeric OHLC values`); continue; }
    if (h < l) { warnings.push(`Row ${i + 2}: high < low, skipped`); continue; }
    if (h < Math.max(o, c) - 0.001 || l > Math.min(o, c) + 0.001) {
      warnings.push(`Row ${i + 2}: inconsistent OHLC (high/low don't contain open/close)`);
    }

    candles.push({ ts, open: o, high: h, low: l, close: c, volume: v });
  }

  candles.sort((a, b) => a.ts - b.ts);
  return { data: candles, errors: errors.slice(0, 8), warnings: warnings.slice(0, 5) };
}

// ─── Options OI Parser ────────────────────────────────────────────────────────
/**
 * Required: strike
 * Optional: call_oi, put_oi, gamma_estimate, call_volume, put_volume, expiration
 */
export function parseOptionsCsv(text: string): ParseResult<OptionRow> {
  const errors: string[] = [];
  const warnings: string[] = [];

  const parsed: PapaParseResult<Record<string, string>> = Papa.parse(text.trim(), {
    header: true, skipEmptyLines: true, dynamicTyping: false
  });

  if (parsed.errors.length) warnings.push(...parsed.errors.slice(0, 3).map(e => `Row ${e.row}: ${e.message}`));

  const headers = parsed.meta.fields?.map(h => h.toLowerCase().replace(/[\s_'"]+/g, '_')) || [];
  const findCol = (...names: string[]) => {
    for (const name of names) {
      const idx = headers.findIndex(h => h === name || h.includes(name));
      if (idx >= 0) return parsed.meta.fields![idx];
    }
    return null;
  };

  const sCol  = findCol('strike', 'strike_price', 'expiry_strike');
  const coCol = findCol('call_oi', 'calloi', 'c_oi', 'call_open_interest');
  const poCol = findCol('put_oi',  'putoi',  'p_oi', 'put_open_interest');
  const gCol  = findCol('gamma', 'gamma_estimate', 'net_gamma', 'gamma_exp');
  const cvCol = findCol('call_volume', 'call_vol', 'cv');
  const pvCol = findCol('put_volume',  'put_vol',  'pv');
  const expCol = findCol('expiration', 'exp', 'expiry', 'expiry_date');

  if (!sCol) return { data: [], errors: ['Missing required column: strike'], warnings };

  const options: OptionRow[] = [];
  for (let i = 0; i < parsed.data.length; i++) {
    const row = parsed.data[i];
    const strike = parseFloat(row[sCol!]);
    if (isNaN(strike) || strike <= 0) { errors.push(`Row ${i + 2}: invalid strike "${row[sCol!]}"`); continue; }
    options.push({
      strike,
      callOI:  coCol  ? parseFloat(row[coCol])  || 0 : 0,
      putOI:   poCol  ? parseFloat(row[poCol])   || 0 : 0,
      gamma:   gCol   ? parseFloat(row[gCol])    || null : null,
      callVol: cvCol  ? parseFloat(row[cvCol])   || 0 : 0,
      putVol:  pvCol  ? parseFloat(row[pvCol])   || 0 : 0,
      expiration: expCol ? row[expCol] || null : null,
    });
  }

  options.sort((a, b) => a.strike - b.strike);
  return { data: options, errors: errors.slice(0, 8), warnings: warnings.slice(0, 5) };
}

// ─── Timestamp parser ─────────────────────────────────────────────────────────
function parseTimestamp(s: string): number | null {
  if (!s) return null;
  // Unix seconds or milliseconds
  const n = Number(s);
  if (!isNaN(n)) {
    return n > 1e10 ? n : n * 1000;
  }
  // Try native Date
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.getTime();
  // Try MM/DD/YYYY HH:MM
  const m1 = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (m1) {
    return new Date(parseInt(m1[3]), parseInt(m1[1]) - 1, parseInt(m1[2]), parseInt(m1[4]), parseInt(m1[5]), parseInt(m1[6] || '0')).getTime();
  }
  return null;
}
